import DotLoader from "@/components/ui/dot-loader";
import Phonepad from "@/components/ui/phonepad";
import { ButtonCall, ButtonHangUp } from "@EspaceParkQC/sdk-call-center";
import { Button } from "@EspaceParkQC/sdk-react";
import { DeleteIcon, PhoneOutgoing, XIcon } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function NewCall(): React.ReactElement {
  const { t } = useTranslation("main");
  const [targetExtension, setTargetExtension] = useState("");
  const callHandlerRef = useRef<{
    onClick: () => void;
    isAvailable: boolean;
  } | null>(null);

  const handleKeyPress = (digit: string) => {
    setTargetExtension((prev) => prev + digit);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle number keys (0-9) and special keys (*, #)
      if (/^[0-9*#]$/.test(event.key)) {
        event.preventDefault();
        setTargetExtension((prev) => prev + event.key);
      }
      // Handle backspace
      else if (event.key === "Backspace") {
        event.preventDefault();
        setTargetExtension((prev) => prev.slice(0, -1));
      }
      // Handle Enter to trigger call
      else if (event.key === "Enter" && callHandlerRef.current && targetExtension) {
        const { onClick, isAvailable } = callHandlerRef.current;
        if (isAvailable) {
          event.preventDefault();
          onClick();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [targetExtension]);

  return (
    <ButtonCall
      targetExtension={Number(targetExtension)}
      render={({ onClick, onKeyDown, isAvailable, isCalling }) => {
        // Store handlers for global keyboard listener
        callHandlerRef.current = { onClick, isAvailable };

        if (isCalling) {
          return (
            <div className="max-w-md w-full px-8 py-12 border border-border rounded-xl shadow-xl">
              <div className="flex flex-col items-center justify-center gap-8">
                <DotLoader className="text-primary" />
                <p className="text-sm font-semibold text-center">
                  {targetExtension ? t("new-call.calling-extension", { extension: targetExtension }) : t("new-call.calling")}
                </p>
                <ButtonHangUp
                  render={({ onClick, isAvailable }) =>
                    isAvailable ? (
                      <Button variant="destructive" onClick={onClick}>
                        {t("new-call.hang-up")}
                      </Button>
                    ) : (
                      <Fragment />
                    )
                  }
                />
              </div>
            </div>
          );
        }

        return (
          <div className="max-w-md w-full p-8 border border-border rounded-xl shadow-xl">
            <div className="flex items-center justify-start gap-4 mb-8">
              <div className="bg-primary/10 rounded-full size-10 flex items-center justify-center">
                <PhoneOutgoing className="size-4 text-primary -ml-0.5" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-left">{t("new-call.call-extension")}</p>
                <p className="text-xs text-muted-foreground text-left">{t("new-call.type-extension")}</p>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-4">
              <Phonepad onKeyPress={handleKeyPress} isAvailable={true} />
              <div className="flex items-center justify-between gap-4 w-full">
                <Button
                  onClick={() => {
                    onClick();
                    setTargetExtension("");
                  }}
                  onKeyDown={onKeyDown}
                  disabled={!isAvailable || !targetExtension}
                  className="flex-1"
                >
                  {targetExtension ? t("new-call.call-button", { extension: targetExtension }) : t("new-call.type-extension-button")}
                </Button>
                <Button variant="outline" onClick={() => setTargetExtension("")} disabled={!targetExtension}>
                  <DeleteIcon className="size-4" />
                </Button>
                <Button variant="outline" onClick={() => setTargetExtension("")} disabled={!targetExtension}>
                  <XIcon className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
